import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CreditCard,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Loader,
  ExternalLink
} from 'lucide-react'
import api from '../config/api'
import { formatDate } from '../utils/formatDate'
import '../styles/Payments.css'

function Payments() {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedInvoice, setExpandedInvoice] = useState(null)
  const [payingInvoiceId, setPayingInvoiceId] = useState(null)
  const [paymentMessage, setPaymentMessage] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    // Check for payment return status in URL params
    const status = searchParams.get('status')
    const invoiceId = searchParams.get('invoice')

    if (status === 'success') {
      setPaymentMessage({
        type: 'success',
        text: 'Payment successful! Your invoice has been updated. It may take a moment to reflect.'
      })
      // Clean URL params
      setSearchParams({})
    } else if (status === 'cancelled') {
      setPaymentMessage({
        type: 'info',
        text: 'Payment was cancelled. You can try again anytime.'
      })
      setSearchParams({})
    }

    fetchInvoices()
  }, [])

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      const response = await api.get('/invoices')
      setInvoices(response.data.data)
    } catch (err) {
      setError('Failed to load payment information')
    } finally {
      setLoading(false)
    }
  }

  const handlePayNow = async (invoiceId) => {
    try {
      setPayingInvoiceId(invoiceId)
      setPaymentMessage(null)
      setError('')

      const response = await api.post('/checkout/create-session', { invoiceId })

      if (response.data.success && response.data.data.checkoutUrl) {
        // Redirect to PayMongo checkout
        window.location.href = response.data.data.checkoutUrl
      } else {
        setError('Failed to initiate payment. Please try again.')
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to initiate payment. Please try again.'
      setError(message)
    } finally {
      setPayingInvoiceId(null)
    }
  }

  const outstandingInvoices = invoices.filter(inv => inv.status !== 'paid')
  const paidInvoices = invoices.filter(inv => inv.status === 'paid')

  const totalOutstanding = outstandingInvoices
    .reduce((sum, inv) => sum + (inv.balance || inv.amount || 0), 0)

  const totalPaid = paidInvoices
    .reduce((sum, inv) => sum + (inv.amount || 0), 0)

  const nextDueInvoice = outstandingInvoices
    .filter(inv => inv.dueDate)
    .sort((a, b) => {
      const dateA = a.dueDate?._seconds || a.dueDate?.seconds || new Date(a.dueDate).getTime() / 1000
      const dateB = b.dueDate?._seconds || b.dueDate?.seconds || new Date(b.dueDate).getTime() / 1000
      return dateA - dateB
    })[0]

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return CheckCircle
      case 'pending': return Clock
      case 'overdue': return AlertCircle
      default: return CreditCard
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'paid': return 'status-paid'
      case 'pending': return 'status-pending'
      case 'overdue': return 'status-overdue'
      default: return ''
    }
  }

  return (
    <div className="payments-page">
      <header className="page-header">
        <div className="header-content">
          <CreditCard size={28} />
          <div>
            <h1>Payments</h1>
            <p>View balances and pay online</p>
          </div>
        </div>
      </header>

      {/* Payment Status Messages */}
      {paymentMessage && (
        <div className={`payment-notice ${paymentMessage.type === 'success' ? 'payment-success' : ''}`}>
          {paymentMessage.type === 'success' ? <CheckCircle size={20} /> : <Info size={20} />}
          <p>{paymentMessage.text}</p>
          <button
            className="dismiss-btn"
            onClick={() => setPaymentMessage(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {/* Payment Summary */}
      <div className="payments-summary">
        <div className="summary-card outstanding">
          <DollarSign size={24} />
          <div>
            <span className="summary-label">Total Outstanding</span>
            <span className="summary-value">{totalOutstanding.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
          </div>
        </div>
        <div className="summary-card paid">
          <CheckCircle size={24} />
          <div>
            <span className="summary-label">Total Paid</span>
            <span className="summary-value">{totalPaid.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
          </div>
        </div>
        <div className="summary-card next-due">
          <Clock size={24} />
          <div>
            <span className="summary-label">Next Due Date</span>
            <span className="summary-value">
              {nextDueInvoice ? formatDate(nextDueInvoice.dueDate) : 'None'}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading payments...</p>
        </div>
      ) : (
        <>
          {/* Outstanding Invoices */}
          <section className="payments-section">
            <h2 className="section-title">
              <AlertCircle size={20} />
              Outstanding Invoices
              {outstandingInvoices.length > 0 && (
                <span className="section-count">{outstandingInvoices.length}</span>
              )}
            </h2>

            {outstandingInvoices.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={48} />
                <h3>All caught up</h3>
                <p>You have no outstanding invoices</p>
              </div>
            ) : (
              <div className="invoices-list">
                {outstandingInvoices.map((invoice, index) => {
                  const StatusIcon = getStatusIcon(invoice.status)
                  const isExpanded = expandedInvoice === invoice.id
                  const isPaying = payingInvoiceId === invoice.id

                  return (
                    <motion.div
                      key={invoice.id}
                      className={`invoice-card ${getStatusClass(invoice.status)}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div
                        className="invoice-header"
                        onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id)}
                      >
                        <div className="invoice-main">
                          <StatusIcon size={20} className="status-icon" />
                          <div className="invoice-info">
                            <span className="invoice-number">{invoice.invoiceNumber}</span>
                            <span className="invoice-date">
                              Due: {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="invoice-amount">
                          <span className="amount">
                            {(invoice.balance || invoice.amount)?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}
                          </span>
                          <span className={`status-badge ${getStatusClass(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </div>
                        <button className="expand-btn">
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                      </div>

                      {isExpanded && (
                        <motion.div
                          className="invoice-details"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          {invoice.items?.length > 0 && (
                            <div className="invoice-items">
                              <h4>Line Items</h4>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Rate</th>
                                    <th>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoice.items.map((item, i) => (
                                    <tr key={i}>
                                      <td>{item.description}</td>
                                      <td>{item.quantity}</td>
                                      <td>{Number(item.rate).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</td>
                                      <td>{(item.quantity * item.rate).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div className="invoice-footer">
                            <div className="invoice-totals">
                              <div className="total-row">
                                <span>Subtotal:</span>
                                <span>{invoice.amount?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
                              </div>
                              {invoice.balance !== invoice.amount && (
                                <div className="total-row balance">
                                  <span>Balance Due:</span>
                                  <span>{invoice.balance?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
                                </div>
                              )}
                            </div>
                            <button
                              className="pay-now-btn"
                              onClick={(e) => {
                                e.stopPropagation()
                                handlePayNow(invoice.id)
                              }}
                              disabled={isPaying}
                            >
                              {isPaying ? (
                                <>
                                  <Loader size={16} className="spinning" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <ExternalLink size={16} />
                                  Pay Now
                                </>
                              )}
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* Quick pay button visible even when not expanded */}
                      {!isExpanded && (
                        <div className="invoice-actions">
                          <button
                            className="pay-now-btn compact"
                            onClick={(e) => {
                              e.stopPropagation()
                              handlePayNow(invoice.id)
                            }}
                            disabled={isPaying}
                          >
                            {isPaying ? (
                              <Loader size={14} className="spinning" />
                            ) : (
                              'Pay Now'
                            )}
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )
                })}
              </div>
            )}
          </section>

          {/* Payment History */}
          <section className="payments-section">
            <h2 className="section-title">
              <CheckCircle size={20} />
              Payment History
              {paidInvoices.length > 0 && (
                <span className="section-count">{paidInvoices.length}</span>
              )}
            </h2>

            {paidInvoices.length === 0 ? (
              <div className="empty-state">
                <CreditCard size={48} />
                <h3>No payment history</h3>
                <p>Completed payments will appear here</p>
              </div>
            ) : (
              <div className="invoices-list">
                {paidInvoices.map((invoice, index) => {
                  const isExpanded = expandedInvoice === invoice.id

                  return (
                    <motion.div
                      key={invoice.id}
                      className="invoice-card status-paid"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div
                        className="invoice-header"
                        onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id)}
                      >
                        <div className="invoice-main">
                          <CheckCircle size={20} className="status-icon" />
                          <div className="invoice-info">
                            <span className="invoice-number">{invoice.invoiceNumber}</span>
                            <span className="invoice-date">
                              Paid: {invoice.paidDate ? formatDate(invoice.paidDate) : formatDate(invoice.updatedAt)}
                            </span>
                          </div>
                        </div>
                        <div className="invoice-amount">
                          <span className="amount">
                            {invoice.amount?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}
                          </span>
                          <div className="payment-meta">
                            {invoice.paymentMethod && (
                              <span className="payment-method">{invoice.paymentMethod}</span>
                            )}
                            {invoice.receiptNumber && (
                              <span className="receipt-number">#{invoice.receiptNumber}</span>
                            )}
                          </div>
                        </div>
                        <button className="expand-btn">
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                      </div>

                      {isExpanded && (
                        <motion.div
                          className="invoice-details"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          {invoice.items?.length > 0 && (
                            <div className="invoice-items">
                              <h4>Line Items</h4>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Rate</th>
                                    <th>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoice.items.map((item, i) => (
                                    <tr key={i}>
                                      <td>{item.description}</td>
                                      <td>{item.quantity}</td>
                                      <td>{Number(item.rate).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</td>
                                      <td>{(item.quantity * item.rate).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div className="invoice-footer">
                            <div className="invoice-totals">
                              <div className="total-row">
                                <span>Amount Paid:</span>
                                <span>{invoice.amount?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
                              </div>
                              {invoice.paymentMethod && (
                                <div className="total-row">
                                  <span>Method:</span>
                                  <span>{invoice.paymentMethod}</span>
                                </div>
                              )}
                              {invoice.receiptNumber && (
                                <div className="total-row">
                                  <span>Receipt:</span>
                                  <span>#{invoice.receiptNumber}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  )
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

export default Payments
